import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    Grid,
    InputAdornment
} from "@mui/material";
import {
    PersonAdd as PersonAddIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Person as PersonIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Security as SecurityIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    serverTimestamp,
    addDoc
} from "firebase/firestore";

function UserRoleManagement({ user, orgId }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", severity: "info" });
    const [searchQuery, setSearchQuery] = useState("");
    const [currentAdminCount, setCurrentAdminCount] = useState(0);

    // State for the role change dialog
    const [roleDialog, setRoleDialog] = useState({
        open: false,
        user: null,
        newRole: "",
        confirmText: "",
        required: ""
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, orderBy("role"), orderBy("name"));
            const snapshot = await getDocs(q);

            const usersList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setUsers(usersList);

            // Count current admins
            const adminCount = usersList.filter(u => u.role === "admin").length;
            setCurrentAdminCount(adminCount);

            setMessage({ text: "", severity: "info" });
        } catch (error) {
            console.error("Error fetching users:", error);
            setMessage({
                text: "Failed to load users: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value.toLowerCase());
    };

    const clearSearch = () => {
        setSearchQuery("");
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchQuery) ||
        user.email?.toLowerCase().includes(searchQuery)
    );

    const openRoleDialog = (user, newRole) => {
        let required = "";
        if (newRole === "admin") {
            required = "make-admin";
        } else if (newRole === "employee" && currentAdminCount <= 1 && user.role === "admin") {
            // Can't demote the last admin
            setMessage({
                text: "Cannot demote the last admin. Promote another user to admin first.",
                severity: "error"
            });
            return;
        }

        setRoleDialog({
            open: true,
            user,
            newRole,
            confirmText: "",
            required
        });
    };

    const closeRoleDialog = () => {
        setRoleDialog({
            ...roleDialog,
            open: false
        });
    };

    const handleRoleChange = async () => {
        // Validate confirmation text
        if (roleDialog.required && roleDialog.confirmText !== roleDialog.required) {
            setMessage({
                text: `Please type "${roleDialog.required}" to confirm this action`,
                severity: "error"
            });
            return;
        }

        setLoading(true);
        try {
            const { user: selectedUser, newRole } = roleDialog;

            // Update user role
            const userRef = doc(db, "organizations", orgId, "users", selectedUser.id);
            await updateDoc(userRef, {
                role: newRole,
                roleLastUpdated: serverTimestamp(),
                roleUpdatedBy: user.uid
            });

            // Log the role change
            await addDoc(collection(db, "organizations", orgId, "adminActionLogs"), {
                action: "roleChange",
                targetUserId: selectedUser.id,
                targetUserEmail: selectedUser.email,
                oldRole: selectedUser.role,
                newRole,
                adminId: user.uid,
                adminEmail: user.email,
                timestamp: serverTimestamp()
            });

            // Update local state
            setUsers(prevUsers =>
                prevUsers.map(u =>
                    u.id === selectedUser.id ? { ...u, role: newRole } : u
                )
            );

            // Update admin count
            if (newRole === "admin" && selectedUser.role !== "admin") {
                setCurrentAdminCount(prev => prev + 1);
            } else if (newRole !== "admin" && selectedUser.role === "admin") {
                setCurrentAdminCount(prev => prev - 1);
            }

            setMessage({
                text: `${selectedUser.name || selectedUser.email}'s role has been updated to ${newRole}`,
                severity: "success"
            });

            closeRoleDialog();
        } catch (error) {
            console.error("Error updating user role:", error);
            setMessage({
                text: "Failed to update user role: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const isCurrentUser = (userId) => {
        return userId === user.uid;
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                <SupervisorAccountIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                User Role Management
            </Typography>

            {message.text && (
                <Alert
                    severity={message.severity}
                    sx={{ mb: 2 }}
                    onClose={() => setMessage({ text: "", severity: "info" })}
                >
                    {message.text}
                </Alert>
            )}

            {/* Search and Filter Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            placeholder="Search users by name or email"
                            value={searchQuery}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={clearSearch} size="small">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                        {users.length} total users • {currentAdminCount} admins • {users.length - currentAdminCount} employees
                    </Typography>

                    <Button
                        variant="outlined"
                        onClick={fetchUsers}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    >
                        Refresh
                    </Button>
                </Box>
            </Paper>

            {/* Users Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Current Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <CircularProgress size={24} sx={{ my: 2 }} />
                                </TableCell>
                            </TableRow>
                        ) : filteredUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No users found matching your search criteria.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredUsers.map(userItem => (
                                <TableRow key={userItem.id}>
                                    <TableCell>
                                        {userItem.name || "No Name"}
                                        {isCurrentUser(userItem.id) && (
                                            <Chip
                                                label="You"
                                                size="small"
                                                color="primary"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>{userItem.email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={userItem.role === "admin" ? <SecurityIcon /> : <PersonIcon />}
                                            label={userItem.role === "admin" ? "Admin" : "Employee"}
                                            color={userItem.role === "admin" ? "primary" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {userItem.name === "Pending Invite" ? (
                                            <Chip label="Pending" color="warning" size="small" />
                                        ) : (
                                            <Chip label="Active" color="success" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {userItem.name !== "Pending Invite" && (
                                            <>
                                                {userItem.role === "employee" ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => openRoleDialog(userItem, "admin")}
                                                        disabled={isCurrentUser(userItem.id)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Make Admin
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => openRoleDialog(userItem, "employee")}
                                                        disabled={isCurrentUser(userItem.id) || (currentAdminCount <= 1)}
                                                    >
                                                        Make Employee
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Role Change Dialog */}
            <Dialog open={roleDialog.open} onClose={closeRoleDialog}>
                <DialogTitle>
                    <SupervisorAccountIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Change User Role
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {roleDialog.newRole === "admin" ? (
                            <>
                                You are about to promote <strong>{roleDialog.user?.name || roleDialog.user?.email}</strong> to
                                an admin role. This will give them full administrative access to this organization.
                            </>
                        ) : (
                            <>
                                You are about to change <strong>{roleDialog.user?.name || roleDialog.user?.email}</strong> from
                                admin to employee role. They will lose administrative access to this organization.
                            </>
                        )}
                    </DialogContentText>

                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="subtitle2">User Details:</Typography>
                        <Typography variant="body2">
                            <strong>Name:</strong> {roleDialog.user?.name || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Email:</strong> {roleDialog.user?.email}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Current Role:</strong> {roleDialog.user?.role}
                        </Typography>
                        <Typography variant="body2">
                            <strong>New Role:</strong> {roleDialog.newRole}
                        </Typography>
                    </Box>

                    {roleDialog.required && (
                        <TextField
                            fullWidth
                            label={`Type "${roleDialog.required}" to confirm`}
                            value={roleDialog.confirmText}
                            onChange={(e) => setRoleDialog({ ...roleDialog, confirmText: e.target.value })}
                            required
                            sx={{ mt: 2 }}
                            error={roleDialog.confirmText && roleDialog.confirmText !== roleDialog.required}
                            helperText={
                                roleDialog.confirmText &&
                                roleDialog.confirmText !== roleDialog.required &&
                                "Text doesn't match"
                            }
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRoleDialog}>Cancel</Button>
                    <Button
                        onClick={handleRoleChange}
                        variant="contained"
                        color={roleDialog.newRole === "admin" ? "primary" : "warning"}
                        disabled={
                            loading ||
                            (roleDialog.required && roleDialog.confirmText !== roleDialog.required)
                        }
                    >
                        {loading ? <CircularProgress size={24} /> : "Confirm Role Change"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default UserRoleManagement;